import type { ComponentProps, ReactElement, ReactNode } from "react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export const DRAWER_SIZES = ["sm", "md", "lg"] as const;
export type DrawerSize = (typeof DRAWER_SIZES)[number];

/**
 * Desktop width. Below `lg` the drawer is always full screen.
 * - `sm` — settings lookups (cargos, statuses)
 * - `md` — entity forms (payables, receivables, service front, documents, catalog add)
 * - `lg` — dense editors (project, partner, client budget, cost plan, purchasing, diary report model)
 */
function drawerSizeClassName(size: DrawerSize): string {
  switch (size) {
    case "sm":
      return "sm:max-w-lg sm:data-[side=right]:w-full sm:data-[side=left]:w-full";
    case "md":
      return "sm:max-w-2xl sm:data-[side=right]:w-full sm:data-[side=left]:w-full";
    case "lg":
      return "sm:max-w-5xl sm:data-[side=right]:w-full sm:data-[side=left]:w-full";
    default: {
      const _exhaustive: never = size;
      return _exhaustive;
    }
  }
}

/** Right-side detail / filter panel. Built on {@link Sheet} with shared layout defaults. */
function Drawer(props: ComponentProps<typeof Sheet>): ReactElement {
  return <Sheet {...props} />;
}

type DrawerContentProps = ComponentProps<typeof SheetContent> & {
  size?: DrawerSize;
};

/** Drawer shell — full viewport below `lg`; inset side panel from `lg` up. */
function DrawerContent({
  className,
  side = "right",
  size = "sm",
  ...props
}: DrawerContentProps): ReactElement {
  return (
    <SheetContent
      side={side}
      className={cn(
        "flex flex-col gap-0 p-0",
        drawerSizeClassName(size),
        "max-lg:min-h-0 max-lg:!h-dvh max-lg:!max-h-dvh",
        className,
      )}
      {...props}
    />
  );
}

type DrawerBodyProps = ComponentProps<"div">;

/** Scrollable drawer main content with standard horizontal padding and top gap below the header. */
function DrawerBody({ className, ...props }: DrawerBodyProps): ReactElement {
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        "px-4 py-4",
        // Mobile: flex middle pane in full-screen shell.
        "max-lg:min-h-0 max-lg:flex-1 max-lg:basis-0 max-lg:overflow-y-auto max-lg:overscroll-y-contain max-lg:touch-pan-y",
        // Desktop: sheet shell has fixed height; fill and scroll.
        "lg:min-h-0 lg:flex-1 lg:overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
}

const DrawerClose = SheetClose;
const DrawerDescription = SheetDescription;
const DrawerFooter = SheetFooter;
const DrawerHeader = SheetHeader;
const DrawerTitle = SheetTitle;

type DetailDrawerFooterProps = {
  /** Primary action(s) on the right (Edit, Save, extra outline actions). */
  primaryAction?: ReactNode;
  /** Optional secondary action on the left (Delete / Cancel). */
  secondaryAction?: ReactNode;
  className?: string;
};

/**
 * Shared drawer footer. Secondary left, primary cluster right.
 * Buttons hug their label on desktop and share the row on mobile — callers
 * should not pass `flex-1`, `stacked`, or `w-full sm:w-auto`.
 */
function DetailDrawerFooter({
  primaryAction,
  secondaryAction,
  className,
}: DetailDrawerFooterProps): ReactElement {
  return (
    <DrawerFooter
      className={cn(
        "flex-row items-center gap-2 sm:justify-between",
        "max-sm:[&>*]:min-w-0 max-sm:[&>*]:flex-1",
        "[&_[data-slot=button]]:min-w-0",
        "max-sm:[&_[data-slot=button]]:flex-1!",
        "sm:[&_[data-slot=button]]:w-auto! sm:[&_[data-slot=button]]:flex-none!",
        "max-md:[&_[data-slot=button]]:flex-row!",
        className,
      )}
    >
      {secondaryAction ?? <span className="hidden sm:block" />}
      {primaryAction ? (
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:flex-none">
          {primaryAction}
        </div>
      ) : (
        <span className="hidden sm:block" />
      )}
    </DrawerFooter>
  );
}

type DetailDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Body content — typically a {@link DrawerBody}. */
  children: ReactNode;
  /** Optional footer — typically a {@link DetailDrawerFooter}. */
  footer?: ReactNode;
  /** Desktop width. Defaults to `sm`. */
  size?: DrawerSize;
  contentClassName?: string;
  overlayClassName?: string;
  /**
   * On mobile, use a top-left back arrow instead of the top-right X (the drawer
   * is full-screen there). Desktop keeps the X. Defaults to `true`.
   */
  mobileBack?: boolean;
  /**
   * When `false`, overlay click does not close (create/edit forms). Close with
   * the header X or mobile back. Defaults to `true` (detail drawers).
   */
  dismissOnOutside?: boolean;
  /**
   * When `true`, the body (not the footer) becomes non-interactive — same
   * condition callers already use for footer `disabled={savePending}`.
   */
  bodyDisabled?: boolean;
  /**
   * When `false`, the sheet is non-modal so another dialog (e.g. photo mode)
   * can sit in front. Defaults to `true`.
   */
  modal?: boolean;
};

/**
 * Props-driven detail drawer shell (Drawer + content + titled header + optional
 * footer). Use this for the common case; drop down to {@link Drawer} and the
 * lower-level primitives when a screen needs a custom layout.
 */
function DetailDrawer({
  open,
  onOpenChange,
  title,
  children,
  footer,
  size = "sm",
  contentClassName,
  overlayClassName,
  mobileBack = true,
  dismissOnOutside = true,
  bodyDisabled = false,
  modal = true,
}: DetailDrawerProps): ReactElement {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} modal={modal}>
      <DrawerContent
        size={size}
        className={contentClassName}
        overlayClassName={overlayClassName}
        onPointerDownOutside={dismissOnOutside ? undefined : (event) => event.preventDefault()}
        onInteractOutside={dismissOnOutside ? undefined : (event) => event.preventDefault()}
        onFocusOutside={dismissOnOutside ? undefined : (event) => event.preventDefault()}
      >
        <DrawerHeader mobileBack={mobileBack}>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col",
            bodyDisabled && "opacity-60",
          )}
          // Same pattern as ChoiceGroup: freeze subtree without per-field disabled.
          inert={bodyDisabled ? true : undefined}
          aria-busy={bodyDisabled || undefined}
        >
          {children}
        </div>
        {footer}
      </DrawerContent>
    </Drawer>
  );
}

export {
  DetailDrawer,
  DetailDrawerFooter,
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
};
